import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { StockModel } from "../../Api/Models/stock.model";
import { stockService } from "../../Api/Services/StockService";
import { useInfoModal } from "../../Context/InfoModalProvider";
import { useLoading } from "../../Context/LoadingProvider";
import { weeklyStockCheckService } from "../../Api/Services/WeeklyStockCheckService";
import { CheckIcon } from "../../Components/Icons/CheckIcon";
import { HomeIcon } from "../../Components/Icons/HomeIcon";

interface WeeklyStockCheckProps {}

const WeeklyStockCheck: React.FunctionComponent<WeeklyStockCheckProps> = () => {
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();

  const [stocks, setStocks] = useState<StockModel[]>([]);
  const { startLoading, finnishLoading } = useLoading();
  const [stockCorrectionProductId, setStockCorrectionProductId] = useState<
    number | null
  >(null);
  const { showInfo } = useInfoModal();

  const stockCorrectionTextRef = useRef<HTMLTextAreaElement | null>(null);
  const stockCorrectionNumberRef = useRef<HTMLInputElement | null>(null);
  const [selectedProductVariation, setSelectedProductVariation] = useState<
    number | null
  >(null);

  const [createdStockCorrections, setCreatedStockCorrections] = useState<
    { productId: number; quantity: string; variationId: number | null }[]
  >([]);

  const [checkedStocks, setCheckedStocks] = useState<number[]>([]);
  const [savedChckedStocks, setSavedChckedStocks] = useState<number[]>([]);

  useEffect(() => {
    loadStocks();
    loadCheckedItems();
    //eslint-disable-next-line
  }, []);

  const loadStocks = () => {
    startLoading("get-stock-list");
    stockService
      .listStocks()
      .then((stocks) => {
        setStocks(stocks);
      })
      .finally(() => {
        finnishLoading("get-stock-list");
      });
  };

  const loadCheckedItems = () => {
    startLoading("get-checked-products");
    weeklyStockCheckService
      .thisWeekCheckedProducts()
      .then((products) => {
        setSavedChckedStocks(products.productIds);
      })
      .finally(() => {
        finnishLoading("get-checked-products");
      });
  };

  const sendStockCorrectionRequest = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (
      stockCorrectionProductId &&
      stockCorrectionTextRef.current &&
      stockCorrectionNumberRef.current
    ) {
      startLoading("send-stock-correction");

      stockService
        .stockCorrectionRequest(
          stockCorrectionProductId,
          selectedProductVariation,
          +stockCorrectionNumberRef.current.value,
          stockCorrectionTextRef.current?.value
        )
        .then((stocks) => {
          setStockCorrectionProductId(null);
          if (
            stockCorrectionTextRef.current &&
            stockCorrectionNumberRef.current
          ) {
            setCreatedStockCorrections([
              ...createdStockCorrections,
              {
                productId: stockCorrectionProductId,
                variationId: selectedProductVariation,
                quantity: stockCorrectionNumberRef.current?.value,
              },
            ]);

            stockCorrectionTextRef.current.value = "";
            stockCorrectionNumberRef.current.value = "";
          }

          if (!selectedProductVariation) {
            setCheckedStocks([...checkedStocks, stockCorrectionProductId]);
          }

          setSelectedProductVariation(null);
          showInfo(t("Stock correction request sent!"));
        })
        .finally(() => {
          finnishLoading("send-stock-correction");
        });
    }
  };

  const uniqueProductStocks = (): StockModel[] => {
    let uniqueProductStocks: StockModel[] = [];
    stocks.forEach((stock) => {
      if (!uniqueProductStocks.some((x) => x.productId === stock.productId)) {
        uniqueProductStocks.push(stock);
      }
    });

    return uniqueProductStocks;
  };

  const gTapsRef = useRef<number[]>([]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable)
      ) {
        return;
      }

      if (
        e.code !== "KeyG" ||
        e.ctrlKey ||
        e.altKey ||
        e.metaKey ||
        e.shiftKey
      ) {
        return;
      }

      const now = Date.now();
      const taps = gTapsRef.current.filter((t) => now - t < 800);
      taps.push(now);
      gTapsRef.current = taps;

      if (taps.length < 3) return;
      gTapsRef.current = [];

      const visibleIds = uniqueProductStocks()
        .filter((x) => !savedChckedStocks.includes(x.productId))
        .map((x) => x.productId);

      const allChecked =
        visibleIds.length > 0 &&
        visibleIds.every((id) => checkedStocks.includes(id));

      setCheckedStocks(
        allChecked
          ? checkedStocks.filter((id) => !visibleIds.includes(id))
          : visibleIds
      );

      showInfo(allChecked ? t("All deselected") : t("All selected"));
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    //eslint-disable-next-line
  }, [stocks, savedChckedStocks, checkedStocks]);

  const saveWeeklyStockCheck = () => {
    startLoading("save-weekly-stock-check");

    weeklyStockCheckService
      .store(checkedStocks)
      .then(() => {
        showInfo(t("Weekly stock check successfully saved!"));
        navigate("/menu");
      })
      .catch(() => {
        showInfo(t("There was an error while saveing weekly stock check-in!"));
      })
      .finally(() => {
        finnishLoading("save-weekly-stock-check");
      });
  };

  return (
    <div className="view-container background--light">
      <div
        className={"modal" + (!stockCorrectionProductId ? " hidden" : "")}
        tabIndex={-1}
        aria-modal="true"
        role="dialog"
      >
        <div className="modal__dialog modal-dialog--full-height">
          <div className="modal__header">
            <div className="modal__heading">
              {t("Stock correction request")}
            </div>
            <div
              className="modal__close"
              onClick={() => {
                setStockCorrectionProductId(null);
                if (stockCorrectionTextRef.current) {
                  stockCorrectionTextRef.current.value = "";
                }
              }}
            >
              ✖
            </div>
          </div>
          <form className="modal__body" onSubmit={sendStockCorrectionRequest}>
            {stocks.filter((x) => x.productId === stockCorrectionProductId)
              .length > 1 ? (
              <>
                <label className="text-secondary">{t("Size")}</label>
                <select
                  className="input-field mb-2"
                  onChange={(e) => setSelectedProductVariation(+e.target.value)}
                  required
                >
                  <option value="">-</option>
                  {stocks
                    .filter((x) => x.productId === stockCorrectionProductId)
                    .map((stock) => (
                      <option
                        key={stock.variationId}
                        value={stock.variationId ?? ""}
                      >
                        {stock.variationName}
                      </option>
                    ))}
                </select>
              </>
            ) : null}
            <label className="text-secondary">{t("Updated quantity")}</label>
            <input
              type="number"
              step={1}
              min={0}
              max={9999999}
              ref={stockCorrectionNumberRef}
              placeholder={t("Updated quantity") ?? ""}
              className="input-field mb-2"
              required
            />
            <label className="text-secondary">{t("Message")}</label>
            <textarea
              ref={stockCorrectionTextRef}
              placeholder={t("Message") ?? ""}
              className="mb-6 input-field"
            ></textarea>
            <div className="flex justify-center mt-5">
              <button type="submit" className="button ">
                {t("Send")}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="view-content">

        <div className="navigationbar navigationbar--dark mb-6">
          <div className="navigationbar__left">
            <Link to="/menu" className="navigationbar__button">
              <HomeIcon />
            </Link>
          </div>
          <div className="navigationbar__center">
            <div className="navigationbar__title">{t("Weekly stock check-in")}</div>
          </div>
          <div className="navigationbar__right">
          </div>
        </div>


       

        <div className="">
          {/*<div className="flex justify-center mb-8">
              <div className="select-rounded-wrapper">
                <select>
                  <option value="Warehouse # 1">Warehouse # 1</option>
                  <option value="Warehouse # 2">Warehouse # 2</option>
                </select>
              </div>
          </div>*/}
          <div className="card-list mb-4">
            {uniqueProductStocks()
              .filter((x) => !savedChckedStocks.includes(x.productId))
              .map((stock, index) => (
                <div className={"stock-list-card " +
                          (checkedStocks.includes(stock.productId)
                            ? "selected"
                            : ""
              )} key={index}>
                  <div className="stock-list-card__top">
                    
                    <div className="flex gap-2 justify-between items-center">
                      <div className="flex gap-2">
                        <div className="stock-list-card__image">
                          {stock.productImage && (
                            <img
                              src={stock.productImage}
                              height="50px"
                              alt="Product"
                            />
                          )}
                        </div>

                        <div
                          className="stock-list-card__title"
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          {lang === "ar" ? stock.productNameAr : stock.productName}
                        </div>
                      </div>

                      <button
                        type="button"
                        className={
                          checkedStocks.includes(stock.productId)
                            ? "button-product-status-returned"
                            : "button-product-status-default"
                        }
                        onClick={() => {
                          if (checkedStocks.includes(stock.productId)) {
                            setCheckedStocks([
                              ...checkedStocks.filter(
                                (x) => x !== stock.productId
                              ),
                            ]);
                          } else {
                            setCheckedStocks([...checkedStocks, stock.productId]);
                          }
                        }}
                      >
                        {(checkedStocks.includes(stock.productId) &&
                          <CheckIcon />)}
                      </button>
                    </div>

                  </div>
                     

                 
                    
                    {stock.variationId ? (
                      <div className="stock-list-card__variation-infos">
                        {stocks
                          .filter((x) => x.productId === stock.productId)
                          .map((stockVariation) => (
                            <div className={"variation-pcs-info " + ( (stockVariation.quantity +
                                stockVariation.sanitizedQuantity) === 0 ? "variation-pcs-info--disabled" : "")} key={stockVariation.variationId}>
                              <div className="variation-pcs-info__size">{stockVariation.variationName}</div>
                              <div className="variation-pcs-info__quantity">
                              {stockVariation.quantity +
                                stockVariation.sanitizedQuantity}
                              </div>
                            
                           
                              {createdStockCorrections.find(
                                (x) =>
                                  x.productId === stock.productId &&
                                  x.variationId === stockVariation.variationId
                              ) ? (
                                <div className="variation-pcs-info">
                                  <div className="variation-pcs-info__quantity">
                                    {
                                      createdStockCorrections.find(
                                        (x) =>
                                          x.productId === stock.productId &&
                                          x.variationId ===
                                            stockVariation.variationId
                                      )?.quantity
                                    }
                                    &nbsp;pcs
                                  </div>
                                </div>
                              ) : (
                                ""
                              )}
                             
                            </div>
                          ))}
                      </div>
                    ) : 
                      <div className="stock-list-card__variation-infos">
                        {createdStockCorrections.find(
                          (x) =>
                            x.productId === stock.productId &&
                            x.variationId === null
                        ) ? (
                          <div className="variation-pcs-info">
                            <div className="variation-pcs-info__quantity">
                            {
                              createdStockCorrections.find(
                                (x) => x.productId === stock.productId
                              )?.quantity
                            }
                            &nbsp;pcs
                            </div>
                          </div>
                        ) : null}
                      </div> 
                    }

                    <div className="separator-line separator-line--p10"></div>
                
                    <div className="flex justify-between gap-2">
                      <div className="flex gap-1 items-center">
                        <div className="stock-list-card__pc">
                          {stock.variationId
                            ? stocks
                                .filter((x) => x.productId === stock.productId)
                                .reduce((accumulator, object) => {
                                  return (
                                    accumulator +
                                    object.quantity +
                                    object.sanitizedQuantity
                                  );
                                }, 0)
                            : stock.quantity + stock.sanitizedQuantity}{" "}
                          {t("pc")}
                          </div>
                        </div>
                    
                    <p
                      
                      className="link"
                      onClick={() => {
                        setStockCorrectionProductId(stock.productId);
                      }}
                    >
                      {t("Stock correction")}
                    </p>
                  </div>
                </div>
              ))}
          </div>

          <div className="flex justify-center">
            <button
              type="button"
              className="button"
              onClick={() => saveWeeklyStockCheck()}
            >
              {t("Save")}
            </button>
          </div>

          
        </div>
      </div>
    </div>
  );
};

export default WeeklyStockCheck;
